import { expandRitual } from '../utils/expandRitual';
import { collectionGroup, onSnapshot as fbOnSnapshot } from 'firebase/firestore';
import React, { useState, useEffect, useMemo } from 'react';
import { ScheduleRitual, Player } from '../types';
import { db, collection, safeOnSnapshot, doc, setDoc, deleteDoc, getDocs, addDoc, query, where } from '../firebaseConfig';
import { isDoorRestricted } from '../utils/doorGate';
import { isStaff, isStudent, isAdmin } from '../utils/roles';
import { logSisterAction } from '../sisterAudit';
import { checkAccessFor, AccessContext } from '../utils/access';

interface SchedulePopupProps {
  initialFullView?: boolean;
  roomId: string;
  currentUser: Player;
  classroomId?: string;
  classroomIds?: string[];
  accessCtx?: AccessContext;
  onClose: () => void;
  onOpenForge?: () => void;
  onNavigateToRoom?: (roomId: string) => void;
}

const DAYS = ['Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag', 'Søndag'] as const;

const AGE_GROUPS = [
  'hatchlings','sparks','embers','fledglings','drakes','seekers',
  'sentinels','wyverns','stormriders','flamebearers','ascended',
  'vanguards','mystics','elders','parents'
];

const AGE_LABELS: Record<string, string> = {
  hatchlings: '0. kl', sparks: '1. kl', embers: '2. kl', fledglings: '3. kl',
  drakes: '4. kl', seekers: '5. kl', sentinels: '6. kl', wyverns: '7. kl',
  stormriders: '8. kl', flamebearers: '9. kl', ascended: '10. kl',
  vanguards: '1.G', mystics: '2.G', elders: '3.G', parents: 'Forældre'
};

const fmtDate = (ts: number) => new Date(ts).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' });
const toDateStr = (ts: number) => new Date(ts).toISOString().split('T')[0];
const fromDateStr = (s: string, endOfDay = false) =>
  new Date(s + (endOfDay ? 'T23:59:59' : 'T00:00:00')).getTime();

const s = (val: any): string => {
  if (typeof val === 'string') return val;
  if (!val) return '';
  if (typeof val === 'object') return val.da || val.en || Object.values(val).find((v: any) => typeof v === 'string') as string || '';
  return String(val);
};

// ─── SYNLIGHEDSLOGIK ──────────────────────────────────────────────────────────

const DRAGON_TO_GRADE: Record<string, string> = { hatchlings: '0', sparks: '1', embers: '2', fledglings: '3', drakes: '4', seekers: '5', sentinels: '6', wyverns: '7', stormriders: '8', flamebearers: '9', ascended: '10', vanguards: '11', mystics: '12', elders: '13' };
const toGrade = (v: any): string => {
  if (!v) return '';
  const x = String(v).toLowerCase().trim();
  if (DRAGON_TO_GRADE[x]) return DRAGON_TO_GRADE[x];
  let d = '';
  for (const ch of x) { if (ch >= '0' && ch <= '9') d += ch; }
  return d || x;
};
const gradeMatch = (allowed: any, ageGroup: any): boolean => {
  if (!Array.isArray(allowed) || allowed.length === 0) return true;
  if (allowed.includes(ageGroup)) return true;
  const g = toGrade(ageGroup);
  return allowed.some((a: any) => toGrade(a) === g);
};

export const canSeeRitual = (ritual: ScheduleRitual, player: Player): boolean => {
  const isGuardian = isStaff(player.role);
  if (isGuardian) return true;

  if (ritual.isVisible === false) return false;
  if (ritual.hiddenUntil && Date.now() < ritual.hiddenUntil) return false;

  if (ritual.vacationFrom && ritual.vacationUntil) {
    const now = Date.now();
    // Feriemode skjuler IKKE laengere hele dokumentet mens perioden loeber;
    // forekomster i perioden filtreres per dato (Planisphere) / springes over (NextRitualWidget).
  }

  const hasStudentFilter = ritual.allowedStudentIds && ritual.allowedStudentIds.length > 0;
  const hasAgeFilter = ritual.allowedAgeGroups && ritual.allowedAgeGroups.length > 0;

  if (hasStudentFilter || hasAgeFilter) {
    const matchesStudent = hasStudentFilter && ritual.allowedStudentIds!.includes(player.id);
    const matchesAge = hasAgeFilter && gradeMatch(ritual.allowedAgeGroups, player.ageGroup);
    if (!matchesStudent && !matchesAge) return false;
  }

  { // Model 1b: kun doere paa UDVALGTE ELEVER gater
    const gSubj: string[] = (((ritual.linkedSubjects || []).map((l: any) => l && l.subjectId).filter(Boolean)) as string[]).concat((ritual as any).subjectId ? [(ritual as any).subjectId] : []);
    const gGated: string[] = gSubj.filter((sid: string) => isDoorRestricted(sid));
    if (gGated.length > 0) {
      const gOwned: string[] = ((player as any)?.subjectIds || []) as string[];
      const gManualOk = hasStudentFilter ? (ritual.allowedStudentIds || []).includes(player.id) : false;
      if (gGated.some((sid: string) => gOwned.includes(sid)) === false && gManualOk === false) return false;
    }
  }
  if (ritual.accessLevel === 'plus') return player.campusPackage === 'plus';
  if (ritual.accessLevel === 'standard') return player.campusPackage === 'standard' || player.campusPackage === 'plus';

  return true;
};

// ─── NÆSTE RITUAL WIDGET ──────────────────────────────────────────────────────

export const NextRitualWidget: React.FC<{ roomId: string; currentUser: Player; onClick: () => void; onNextRitual?: (time: string) => void; allRooms?: { id: string; name: any }[] }> = ({ roomId, currentUser, onClick, onNextRitual, allRooms = [] }) => {
  const [next, setNext] = useState<ScheduleRitual | null>(null);
  const [nwSchoolVac, setNwSchoolVac] = useState<any[]>([]);
  const [nextDate, setNextDate] = useState<string>('');
  const [nextTime, setNextTime] = useState<string>('');
  const [current, setCurrent] = useState<ScheduleRitual | null>(null);
  const [currentEnd, setCurrentEnd] = useState<string>('');
  const [currentStart, setCurrentStart] = useState<string>('');
  const [nextEnd, setNextEnd] = useState<string>('');

  useEffect(() => {
    const unsubV = safeOnSnapshot(query(collection(db, 'school_years'), where('status', '==', 'published')), (snap: any) => {
      const vacs: any[] = [];
      snap.docs.forEach((d: any) => { const yr = d.data(); (yr.vacations || []).forEach((v: any) => { if (!v.from || !v.to) return; vacs.push({ from: v.from, to: v.to, hideLessons: v.hideLessons }); }); });
      setNwSchoolVac(vacs);
    });
    return () => { try { unsubV && unsubV(); } catch {} };
  }, []);

  useEffect(() => {
    const unsub = safeOnSnapshot(collection(db, `room_schedules/${roomId}/rituals`), (snap: any) => {
      const rituals: ScheduleRitual[] = [];
      snap.forEach((d: any) => {
        const data = { ...d.data(), id: d.id } as ScheduleRitual;
        if (canSeeRitual(data, currentUser)) rituals.push(data);
      });

      const ymdN = (d: Date) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      const now = new Date();
      const fromD = ymdN(now);
      const toDt = new Date(now); toDt.setDate(toDt.getDate() + 120);
      const toD = ymdN(toDt);
      const nowMin = now.getHours() * 60 + now.getMinutes();
      const vacRanges = nwSchoolVac.filter((v: any) => v.hideLessons !== false).map((v: any) => ({ from: v.from, to: v.to }));
      let best: { r: ScheduleRitual; date: string; time: string; endTime: string; startMinutes: number } | null = null;
      let cur: { r: ScheduleRitual; endTime: string; time: string; startMinutes: number } | null = null;
      for (const r of rituals) {
        const skipVLive = !(r as any).visibleInVacations;
        const occs = expandRitual(r as any, fromD, toD, skipVLive ? vacRanges : [], skipVLive);
        for (const o of occs) {
          if (o.date === fromD && o.startMinutes <= nowMin) {
            if (nowMin < o.endMinutes && (!cur || o.startMinutes > cur.startMinutes)) {
              cur = { r, endTime: o.endTime, time: o.time, startMinutes: o.startMinutes };
            }
            continue;
          }
          if (!best || o.date < best.date || (o.date === best.date && o.startMinutes < best.startMinutes)) {
            best = { r, date: o.date, time: o.time, endTime: o.endTime, startMinutes: o.startMinutes };
          }
          break;
        }
      }
      setNext(best ? best.r : null);
      setNextDate(best ? best.date : '');
      setNextTime(best ? best.time : '');
      setCurrent(cur ? cur.r : null);
      setCurrentEnd(cur ? cur.endTime : '');
      setCurrentStart(cur ? cur.time : '');
      setNextEnd(best ? best.endTime : '');
      if (best && best.time) onNextRitual?.(best.time);
    });
    return () => unsub();
  }, [roomId, currentUser, nwSchoolVac]);

  if (!next) return null;
  const ymdR = (d: Date) => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  const nowR = new Date();
  const todayStr = ymdR(nowR);
  const tmD = new Date(nowR); tmD.setDate(tmD.getDate() + 1);
  const tomorrowStr = ymdR(tmD);
  const occDate = nextDate || todayStr;
  const showTime = nextTime || next.time;
  const MON_DA = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
  const dp = occDate.split('-');
  const dateShort = dp.length === 3 ? (parseInt(dp[2], 10) + '. ' + (MON_DA[(parseInt(dp[1], 10) || 1) - 1] || '')) : '';
  const spand = showTime + (nextEnd ? ' \u2013 ' + nextEnd : '');
  const timeLabel = occDate === todayStr
    ? ('\uD83D\uDFE1 I dag kl. ' + spand)
    : occDate === tomorrowStr
    ? ('\uD83D\uDCC5 I morgen kl. ' + spand)
    : ('\uD83D\uDCC5 ' + (next.day ? next.day.slice(0, 3) + ' ' : '') + dateShort + ' kl. ' + spand);
  const isOtherRoom = !!(next as any).targetRoomId && (next as any).targetRoomId !== roomId && String((next as any).targetRoomId) !== "0";
  // Eleven skal kunne se HVOR lektionen holdes — ikke bare at det er "et andet rum".
  const rumNavn = (id: any) => { const r = allRooms.find(x => x.id === id); return r ? s(r.name) : ''; };
  const nextRoomName = isOtherRoom ? rumNavn((next as any).targetRoomId) : '';
  const curRoomName = current && (current as any).targetRoomId && (current as any).targetRoomId !== roomId
    ? rumNavn((current as any).targetRoomId) : '';
  return (
    <div className="space-y-1 w-full">
      {current && (
        <div className="flex items-center gap-2 bg-green-900/40 border-2 border-green-500/50 rounded-2xl px-4 py-2 w-full">
          <span className="text-lg">{'\uD83D\uDFE2'}</span>
          <div className="text-left flex-1 min-w-0">
            <p className="text-[9px] font-black uppercase tracking-widest text-green-300">Nu {'\u2014'} i gang</p>
            <p className="text-sm text-white font-bold leading-tight truncate">{current.title}</p>
            {curRoomName && <p className="text-[9px] text-green-200/70 truncate">📍 {curRoomName}</p>}
          </div>
          <span className="text-[10px] font-mono font-black text-green-300 whitespace-nowrap">{currentStart ? currentStart + ' \u2013 ' + currentEnd : 'til ' + currentEnd}</span>
        </div>
      )}
      <button onClick={onClick}
        className={`flex items-center gap-3 border-2 rounded-2xl px-4 py-3 transition-all w-full ${isOtherRoom ? 'bg-yellow-900/30 border-yellow-500/60 hover:border-yellow-400' : 'bg-indigo-700/50 border-indigo-400/60 hover:border-indigo-300'}`}>
        <span className="text-xl">{isOtherRoom ? '🚪' : '📅'}</span>
        <div className="text-left flex-1">
          <p className={`text-[10px] font-black uppercase tracking-widest ${isOtherRoom ? 'text-yellow-300' : 'text-indigo-200'}`}>
            {isOtherRoom ? 'Næste lektion — andet rum' : 'Næste session'}
          </p>
          <p className="text-sm text-white font-bold leading-tight">{next.title}</p>
          <p className={`text-sm font-mono font-black ${isOtherRoom ? 'text-yellow-400' : 'text-yellow-300'}`}>{timeLabel}</p>
        </div>
      </button>
      {isOtherRoom && (
        <p className="text-[9px] text-yellow-400/70 italic px-2">
          📍 {nextRoomName || 'Et andet rum'} · åbn skemaet og tryk "Gå til rum"
        </p>
      )}
    </div>
  );
};

// ─── FORGE MODAL ──────────────────────────────────────────────────────────────

interface ForgeModalProps {
  roomId: string;
  classroomId?: string;
  classroomIds?: string[];
  accessCtx?: AccessContext;
  existing?: ScheduleRitual;
  editDate?: string;
  currentUser: Player;
  allRooms?: { id: string; name: string }[];
  onClose: () => void;
}

const ForgeModal: React.FC<ForgeModalProps> = ({ roomId, classroomId, classroomIds, accessCtx, existing, editDate, currentUser, allRooms = [], onClose }) => {
  const [form, setForm] = useState<Partial<ScheduleRitual>>(existing || {
    day: 'Mandag', time: '09:00', endTime: '', title: '', description: '',
    type: 'Lektion', accessLevel: 'all', isVisible: true,
    allowedStudentIds: [], allowedAgeGroups: [],
    vacationFrom: 0, vacationUntil: 0,
    assignedGuardianId: '', assignedGuardianName: '',
    repeat: 'weekly', repeatInterval: 1, seriesEnd: '', visibleInVacations: false,
  });
  const [classroomStudents, setClassroomStudents] = useState<Player[]>([]);
  const [guardians, setGuardians] = useState<Player[]>([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState<'basis' | 'adgang' | 'mere'>('basis');
  const [scope, setScope] = useState<'one' | 'future'>('one');
  const [cancelNote, setCancelNote] = useState('');
  const [coverId, setCoverId] = useState('');
  const [statusLabelC, setStatusLabelC] = useState('');
  const [errC, setErrC] = useState('');
  const [copyToRoomId, setCopyToRoomId] = useState('');
  const [copying, setCopying] = useState(false);
  const [notifyOnSave, setNotifyOnSave] = useState(!existing);
  const [hallsData, setHallsData] = useState<{id:string;name:string;subjects:any[]}[]>([]);

  useEffect(() => {
    // Multi-classroom: foretraek classroomIds, fallback til legacy classroomId
    const effClassroomIds: string[] = (classroomIds && classroomIds.length > 0)
      ? classroomIds
      : (classroomId ? [classroomId] : []);
    let cancelled = false;
    const mapAndFilter = (snap: any): Player[] => {
      const loaded: Player[] = [];
      snap.forEach((d: any) => {
        const data = d.data() as any;
        loaded.push({ ...data, id: d.id, wizardName: data.username || data.wizardName || '', realName: data.realName || '' } as Player);
      });
      let filtered = loaded.filter(u => isStudent(u.role));
      // Filtrer gennem checkAccessFor saa kun elever med faktisk room-adgang vises
      if (accessCtx) {
        filtered = filtered.filter(u => checkAccessFor(u, roomId, accessCtx).hasAccess);
      }
      return filtered;
    };
    (async () => {
      let result: Player[] = [];
      if (effClassroomIds.length > 0) {
        // Firestore array-contains-any har max 30 vaerdier
        const snap = await getDocs(query(collection(db, 'users'), where('classIds', 'array-contains-any', effClassroomIds.slice(0, 30))));
        result = mapAndFilter(snap);
      }
      // FALLBACK: intet classroom koblet ELLER koblingen gav 0 elever ->
      // hent alle elever og filtrer paa faktisk room-adgang (checkAccessFor)
      if (result.length === 0 && accessCtx && isStaff(currentUser.role)) {
        const snap = await getDocs(query(collection(db, 'users'), where('role', '==', 'student')));
        result = mapAndFilter(snap);
      }
      if (!cancelled) setClassroomStudents(result);
    })().catch(() => { if (!cancelled) setClassroomStudents([]); });
    return () => { cancelled = true; };
  }, [classroomId, classroomIds, accessCtx, roomId]);

  useEffect(() => {
    // Phase 3: hent classrooms + flat subjects collection i parallel
    Promise.all([
      getDocs(collection(db, 'classrooms')),
      getDocs(collection(db, 'subjects')),
    ]).then(([classSnap, subjSnap]) => {
      const allSubj = subjSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
      setHallsData(classSnap.docs.map(d => {
        const cData = d.data() as any;
        const flatForClass = allSubj.filter((s: any) => s.classroomId === d.id);
        const subjects = flatForClass.length > 0 ? flatForClass : (cData.subjects || []);
        return { id: d.id, name: cData.name || '', subjects };
      }));
    });
    getDocs(query(collection(db, 'users'), where('role', 'in', ['guardian', 'admin', 'superadmin'])))
      .then(snap => {
        const gs: Player[] = [];
        snap.forEach(d => {
          const data = d.data() as any;
          gs.push({ ...data, id: d.id, wizardName: data.username || data.wizardName || data.name || '' } as Player);
        });
        setGuardians(gs);
      });
  }, []);

  const toggleStudentId = (id: string) => {
    const cur = form.allowedStudentIds || [];
    setForm(f => ({ ...f, allowedStudentIds: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] }));
  };

  const toggleAgeGroup = (ag: string) => {
    const cur = form.allowedAgeGroups || [];
    setForm(f => ({ ...f, allowedAgeGroups: cur.includes(ag) ? cur.filter(x => x !== ag) : [...cur, ag] }));
  };

  const hasVacation = !!(form.vacationFrom && form.vacationUntil);
  const vacationActive = hasVacation && Date.now() >= form.vacationFrom! && Date.now() <= form.vacationUntil!;
  const vacationUpcoming = hasVacation && Date.now() < form.vacationFrom!;
  const hasStudentFilter = (form.allowedStudentIds?.length || 0) > 0;
  const hasAgeFilter = (form.allowedAgeGroups?.length || 0) > 0;

  const sendNotifications = async (ritualTitle: string, targetStudentIds?: string[]) => {
    try {
      // Hent elever der skal notificeres
      let recipientIds: string[] = targetStudentIds || [];
      // Multi-classroom: foretraek classroomIds, fallback til legacy classroomId
      const _effClassroomIds: string[] = (classroomIds && classroomIds.length > 0)
        ? classroomIds
        : (classroomId ? [classroomId] : []);
      if (recipientIds.length === 0 && _effClassroomIds.length > 0) {
        const _idsForQuery = _effClassroomIds.slice(0, 30);
        const snap = await getDocs(query(collection(db, 'users'), where('classIds', 'array-contains-any', _idsForQuery)));
        snap.forEach(d => {
          if (!isStudent(d.data().role)) return;
          // Filtrer gennem checkAccessFor saa kun elever med adgang faar besked
          if (accessCtx) {
            const candidate = { id: d.id, ...(d.data() as any) } as Player;
            if (!checkAccessFor(candidate, roomId, accessCtx).hasAccess) return;
          }
          recipientIds.push(d.id);
        });
      }
      await Promise.all(recipientIds.map(uid =>
        addDoc(collection(db, 'notifications'), {
          toUserId: uid,
          type: 'new_ritual',
          title: '📅 Nyt ritual i dit skema',
          body: ritualTitle,
          roomId,
          createdAt: Date.now(),
          read: false,
        })
      ));
    } catch (e) { console.warn('Notifikation fejlede:', e); }
  };

  const handleSave = async () => {
    if (!form.title?.trim() || !form.day || !form.time) return;
    setSaving(true);
    try {
      const id = existing?.id || `${Date.now()}_${roomId}`;
      const payload = {
        ...form, id,
        allowedStudentIds: form.allowedStudentIds || [],
        allowedAgeGroups: form.allowedAgeGroups || [],
        isVisible: form.isVisible !== false,
        vacationFrom: form.vacationFrom || 0,
        vacationUntil: form.vacationUntil || 0,
        assignedGuardianId: form.assignedGuardianId || '',
        assignedGuardianName: form.assignedGuardianName || '',
        ...(form.targetRoomId ? { targetRoomId: form.targetRoomId } : {}),
        sourceRoomId: roomId,
        ...(() => { const eff = (classroomIds && classroomIds.length > 0) ? classroomIds : (classroomId ? [classroomId] : []); return eff.length > 0 ? { groupId: eff[0], classroomId: eff[0], classroomIds: eff } : {}; })(),
        
        ...(existing ? {} : { seriesStart: new Date().toLocaleDateString('sv-SE') }),
      };
      const exRoom = (existing as any)?.sourceRoomId || roomId;
      if (existing && editDate && scope === 'one') {
        const origG = (existing as any).assignedGuardianId || '';
        if (form.assignedGuardianId && form.assignedGuardianId !== origG) {
          setErrC('Vogterskift kr\u00e6ver \u201dDenne + alle efterf\u00f8lgende\u201d \u2014 brug Afl\u00f8ser-feltet for en enkelt dag');
          setSaving(false); return;
        }
        setErrC('');
        const gnm = coverId ? (guardians.find(g => g.id === coverId) ? (s(guardians.find(g => g.id === coverId)!.wizardName) || guardians.find(g => g.id === coverId)!.name || '') : '') : '';
        const exList = (((existing as any).exceptions || []) as any[]).filter(e => e.date !== editDate);
        exList.push({ date: editDate, patch: { time: form.time, endTime: form.endTime, title: form.title },
          ...(cancelNote.trim() ? { note: cancelNote.trim() } : {}),
          ...(coverId ? { coverGuardianId: coverId, coverGuardianName: gnm } : {}),
          ...(statusLabelC.trim() ? { statusLabel: statusLabelC.trim() } : (coverId ? { statusLabel: 'Afl\u00f8ser' } : {})) });
        await setDoc(doc(db, `room_schedules/${exRoom}/rituals`, existing.id), { exceptions: exList }, { merge: true });
      } else if (existing && editDate && scope === 'future') {
        // Model A-split fra Campus
        const prev = new Date(editDate + 'T12:00:00'); prev.setDate(prev.getDate() - 1);
        await setDoc(doc(db, `room_schedules/${exRoom}/rituals`, existing.id), { seriesEnd: prev.toLocaleDateString('sv-SE') }, { merge: true });
        const nid = `${Date.now()}_${roomId}`;
        await setDoc(doc(db, `room_schedules/${roomId}/rituals`, nid), { ...payload, id: nid, seriesStart: editDate });
      } else {
        await setDoc(doc(db, `room_schedules/${roomId}/rituals`, id), payload); logSisterAction(currentUser, "campus", "TID", "GEMT_SKEMA", id, "");
      }

      if (notifyOnSave && !existing) {
        const targets = hasStudentFilter ? form.allowedStudentIds : undefined;
        await sendNotifications(form.title!, targets);
      }
      onClose();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleCopyToRoom = async () => {
    if (!copyToRoomId || !form.title) return;
    setCopying(true);
    try {
      const newId = `${Date.now()}_${copyToRoomId}`;
      await setDoc(doc(db, `room_schedules/${copyToRoomId}/rituals`, newId), {
        ...form, id: newId,
        sourceRoomId: roomId,
        allowedStudentIds: [],
        allowedAgeGroups: [],
        isVisible: true,
        vacationFrom: 0,
        vacationUntil: 0,
        groupId: '',
      });
      alert('✅ Ritual kopieret!');
      setCopyToRoomId('');
    } catch (e) { console.error(e); }
    finally { setCopying(false); }
  };

  const handleDelete = async () => {
    if (!isAdmin(currentUser.role)) return;
    if (!existing?.id || !confirm('Slet dette ritual?')) return;
    await deleteDoc(doc(db, `room_schedules/${roomId}/rituals`, existing.id)); logSisterAction(currentUser, "campus", "KONTROL", "SLETTET_SKEMA", existing.id, "");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[700] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md" onClick={onClose}>
      <div className="w-full max-w-xl bg-[#0a0a1a]/98 border-2 border-indigo-500/30 rounded-[2rem] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}>

        <div className="p-5 border-b border-white/10 bg-indigo-900/20 flex justify-between items-center shrink-0">
          <h3 className="font-cinzel text-yellow-500 uppercase tracking-widest text-sm">
            {existing ? 'Rediger Ritual' : 'Nyt Ritual'}
          </h3>
          <button onClick={onClose} className="text-white/40 hover:text-white text-3xl leading-none">&times;</button>
        </div>

        {/* TABS */}
        <div className="flex border-b border-white/10 shrink-0">
          {(['basis', 'adgang', 'mere'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex-1 py-3 text-[10px] font-black uppercase tracking-widest transition-all ${tab === t ? 'text-yellow-500 border-b-2 border-yellow-500' : 'text-white/30 hover:text-white/60'}`}>
              {t === 'basis' ? '📋 Detaljer' : t === 'adgang' ? '🔒 Synlighed' : '⚙️ Mere'}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 no-scrollbar">

          {/* ─── TAB: BASIS ─── */}
          {tab === 'basis' && (
            <div className="space-y-4">
              <input value={form.title || ''} onChange={e => setForm(f => ({...f, title: e.target.value}))}
                placeholder="Titel på ritual..."
                className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:border-indigo-500/40 text-sm" />

              <textarea value={form.description || ''} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                placeholder="Beskrivelse (valgfri)..."
                rows={2}
                className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white/80 text-sm outline-none focus:border-indigo-500/40 resize-none" />

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-[9px] text-white/40 font-black uppercase">Dag</p>
                  <select value={form.day} onChange={e => setForm(f => ({...f, day: e.target.value as any}))}
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none">
                    {DAYS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div className="space-y-1">
                  <p className="text-[9px] text-white/40 font-black uppercase">Type</p>
                  <select value={form.type} onChange={e => setForm(f => ({...f, type: e.target.value as any}))}
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none">
                    {['Lektion','Ritual','Samling','Event'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-[9px] text-white/40 font-black uppercase">Start</p>
                  <input type="time" value={form.time || ''} onChange={e => setForm(f => ({...f, time: e.target.value}))}
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none" />
                </div>
                <div className="space-y-1">
                  <p className="text-[9px] text-white/40 font-black uppercase">Slut (valgfri)</p>
                  <input type="time" value={form.endTime || ''} onChange={e => setForm(f => ({...f, endTime: e.target.value}))}
                    className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white/80 text-sm outline-none" />
                </div>
              </div>

              {existing && editDate && (
                <div className="space-y-2 p-3 bg-black/30 border border-indigo-500/20 rounded-xl">
                  <div className="flex gap-2">
                    <button onClick={() => setScope('one')} className={`flex-1 py-2 rounded-xl text-[9px] font-black uppercase border ${scope === 'one' ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-black/30 border-white/10 text-white/40'}`}>{'Kun denne dag (' + editDate + ')'}</button>
                    <button onClick={() => setScope('future')} className={`flex-1 py-2 rounded-xl text-[9px] font-black uppercase border ${scope === 'future' ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-black/30 border-white/10 text-white/40'}`}>{'Denne + alle efterf\u00f8lgende'}</button>
                  </div>
                  {errC && <p className="text-[10px] text-red-400 font-bold">{errC}</p>}
                  {scope === 'one' && (<>
                    <select value={coverId} onChange={e => setCoverId(e.target.value)} className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none">
                      <option value="">{'\u2014 Afl\u00f8ser denne dag (valgfri) \u2014'}</option>
                      {guardians.filter(g => g.id !== form.assignedGuardianId).map(g => <option key={g.id} value={g.id}>{s(g.wizardName) || g.name}</option>)}
                    </select>
                    <div className="flex gap-1 flex-wrap">{['Afl\u00f8ser', 'Selvst\u00e6ndigt arbejde', 'Flyttet'].map(sl => (<button key={sl} onClick={() => setStatusLabelC(statusLabelC === sl ? '' : sl)} className={'px-2 py-1 rounded-lg text-[9px] font-black uppercase border ' + (statusLabelC === sl ? 'bg-amber-600 border-amber-400 text-white' : 'bg-black/30 border-white/10 text-white/40')}>{sl}</button>))}
                      <input value={statusLabelC} onChange={e => setStatusLabelC(e.target.value)} maxLength={24} placeholder="frit..." className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-white text-[10px] w-24 outline-none" /></div>
                    <input value={cancelNote} onChange={e => setCancelNote(e.target.value)} maxLength={140} placeholder={'Besked til elever (valgfri)...'} className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none" />
                    <div className="flex gap-2">
                      <button onClick={async () => { const exRoom = (existing as any).sourceRoomId || roomId; const exList = (((existing as any).exceptions || []) as any[]).filter(e => e.date !== editDate); exList.push({ date: editDate, cancelled: true, ...(cancelNote.trim() ? { note: cancelNote.trim() } : {}), ...(statusLabelC.trim() ? { statusLabel: statusLabelC.trim() } : {}) }); await setDoc(doc(db, `room_schedules/${exRoom}/rituals`, existing.id), { exceptions: exList }, { merge: true }); onClose(); }} className="flex-1 py-2 rounded-xl text-[9px] font-black uppercase border border-red-500/40 text-red-400 hover:bg-red-900/20">{'Aflys denne dag'}</button>
                      {(((existing as any).exceptions || []) as any[]).some(e => e.date === editDate) && (
                        <button onClick={async () => { const exRoom = (existing as any).sourceRoomId || roomId; const exList = (((existing as any).exceptions || []) as any[]).filter(e => e.date !== editDate); await setDoc(doc(db, `room_schedules/${exRoom}/rituals`, existing.id), { exceptions: exList }, { merge: true }); onClose(); }} className="flex-1 py-2 rounded-xl text-[9px] font-black uppercase border border-green-500/40 text-green-400 hover:bg-green-900/20">{'Genopret denne dag'}</button>
                      )}
                    </div>
                  </>)}
                </div>
              )}
              <div className="space-y-1">
                <p className="text-[9px] text-white/40 font-black uppercase">Gentagelse</p>
                <div className="flex gap-2">
                  {(['none','weekly','daily'] as const).map(r => (
                    <button key={r} onClick={() => setForm(f => ({...f, repeat: r}))}
                      className={`flex-1 py-2 rounded-xl text-[9px] font-black uppercase transition-all border ${form.repeat === r ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-black/30 border-white/10 text-white/40'}`}>
                      {r === 'none' ? 'Én gang' : r === 'weekly' ? 'Ugentlig' : 'Daglig'}
                    </button>
                  ))}
                </div>
              </div>

              {form.repeat !== 'none' && (
                <div className="grid grid-cols-3 gap-2">
                  <div><p className="text-[9px] text-white/40 font-black uppercase mb-1">Hver</p>
                    <input type="number" min={1} max={8} value={(form as any).repeatInterval || 1} onChange={e => setForm(f => ({...f, repeatInterval: Number(e.target.value) || 1} as any))} className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none" /></div>
                  <div className="col-span-2"><p className="text-[9px] text-white/40 font-black uppercase mb-1">Serie slutter (valgfri)</p>
                    <input type="date" value={(form as any).seriesEnd || ''} onChange={e => setForm(f => ({...f, seriesEnd: e.target.value} as any))} className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none" /></div>
                </div>
              )}
              <label className="flex items-center gap-2 text-[10px] text-white/60 cursor-pointer">
                <input type="checkbox" checked={!!(form as any).visibleInVacations} onChange={e => setForm(f => ({...f, visibleInVacations: e.target.checked} as any))} /> {'Vis i skole\u00e5rets ferieperioder'}
              </label>
              {/* GUARDIAN TILKNYTNING */}
              <div className="space-y-2">
                <p className="text-[9px] text-white/40 font-black uppercase">🧑‍🏫 Ansvarlig vogter (valgfri)</p>
                <select
                  value={form.assignedGuardianId || ''}
                  onChange={e => {
                    const g = guardians.find(g => g.id === e.target.value);
                    setForm(f => ({
                      ...f,
                      assignedGuardianId: e.target.value,
                      assignedGuardianName: g ? (s(g.wizardName) || g.name || '') : '',
                    }));
                  }}
                  className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-indigo-500/40"
                >
                  <option value="">— Ingen specifik vogter —</option>
                  {guardians.map(g => (
                    <option key={g.id} value={g.id}>{s(g.wizardName) || g.name}</option>
                  ))}
                </select>
                {form.assignedGuardianName && (
                  <p className="text-[9px] text-indigo-300/60 italic">Vises som: "Lektion med {form.assignedGuardianName}"</p>
                )}
              </div>
              <div className="space-y-2">
                <p className="text-[9px] text-white/40 font-black uppercase">🚪 Foregår i andet rum (valgfrit)</p>
                <select
                  value={form.targetRoomId || ''}
                  onChange={e => setForm(f => ({ ...f, targetRoomId: e.target.value || undefined }))}
                  className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-yellow-500/40"
                >
                  <option value="">— Dette rum (standard) —</option>
                  {allRooms.filter(r => r.id !== roomId).map(r => (
                    <option key={r.id} value={r.id}>{s(r.name)}</option>
                  ))}
                </select>
                {form.targetRoomId && <p className="text-[9px] text-yellow-400/60 italic">Elever vil se en "Gå til rum"-knap i skemaet</p>}
              </div>
            </div>
          )}

          {/* ─── TAB: ADGANG ─── */}
          {tab === 'adgang' && (
            <div className="space-y-5">

              {/* PAKKETYPE */}
              <div className="space-y-2">
                <p className="text-[10px] font-black uppercase text-white/45">Pakke-adgang</p>
                <div className="flex gap-2">
                  {([
                    { val: 'all', label: 'Alle', color: 'indigo' },
                    { val: 'basis', label: '🌱 Basis', color: 'green' },
                    { val: 'standard', label: '📦 Standard', color: 'blue' },
                    { val: 'plus', label: '💎 Plus', color: 'amber' },
                  ] as const).map(opt => (
                    <button key={opt.val} onClick={() => setForm(f => ({...f, accessLevel: opt.val}))}
                      className={`flex-1 py-2 rounded-xl text-[9px] font-black uppercase transition-all border ${
                        form.accessLevel === opt.val
                          ? opt.color === 'amber' ? 'bg-amber-600 border-amber-400 text-black'
                          : opt.color === 'blue' ? 'bg-blue-600 border-blue-400 text-white'
                          : opt.color === 'green' ? 'bg-green-700 border-green-500 text-white'
                          : 'bg-indigo-600 border-indigo-400 text-white'
                          : 'bg-black/30 border-white/10 text-white/40'
                      }`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="text-[8px] text-white/20 italic">
                  {form.accessLevel === 'all' ? 'Alle pakker kan se dette'
                    : form.accessLevel === 'basis' ? 'Kun basis+ pakker'
                    : form.accessLevel === 'standard' ? 'Kun standard og plus pakker'
                    : 'Kun plus-pakke'}
                </p>
              </div>

              {/* SYNLIGHED TOGGLE */}
              <div className="flex items-center justify-between bg-black/30 border border-white/10 rounded-xl px-4 py-3">
                <div>
                  <p className="text-[10px] font-black uppercase text-white/70">Synlig for elever</p>
                  <p className="text-[9px] text-white/30">Skjul fra skemaet (guardians ser det altid)</p>
                </div>
                <button onClick={() => setForm(f => ({...f, isVisible: !f.isVisible}))}
                  className={`w-12 h-6 rounded-full relative transition-all ${form.isVisible !== false ? 'bg-green-600' : 'bg-red-700'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow-sm ${form.isVisible !== false ? 'left-6' : 'left-0.5'}`} />
                </button>
              </div>

              {/* FERIEMODE */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-black uppercase text-white/45">🏖️ Planlagt feriemode</p>
                  {hasVacation && (
                    <button onClick={() => setForm(f => ({...f, vacationFrom: 0, vacationUntil: 0}))}
                      className="text-[9px] text-white/30 hover:text-red-400 transition-all">Ryd</button>
                  )}
                </div>
                <p className="text-[9px] text-white/25">Skjules automatisk for elever i perioden.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <p className="text-[9px] text-white/40 font-black uppercase">Fra</p>
                    <input type="date"
                      value={form.vacationFrom ? toDateStr(form.vacationFrom) : ''}
                      onChange={e => setForm(f => ({...f, vacationFrom: e.target.value ? fromDateStr(e.target.value) : 0}))}
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-orange-500/40" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-[9px] text-white/40 font-black uppercase">Til</p>
                    <input type="date"
                      value={form.vacationUntil ? toDateStr(form.vacationUntil) : ''}
                      onChange={e => setForm(f => ({...f, vacationUntil: e.target.value ? fromDateStr(e.target.value, true) : 0}))}
                      className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-orange-500/40" />
                  </div>
                </div>
                {hasVacation && (
                  <div className={`rounded-xl px-4 py-2.5 border flex items-center gap-3 ${
                    vacationActive ? 'bg-orange-950/50 border-orange-500/30 text-orange-300'
                    : vacationUpcoming ? 'bg-yellow-950/40 border-yellow-500/20 text-yellow-300'
                    : 'bg-white/5 border-white/10 text-white/30'}`}>
                    <span className="text-base shrink-0">{vacationActive ? '🏖️' : vacationUpcoming ? '📅' : '✅'}</span>
                    <div>
                      <p className="text-[9px] font-black uppercase">
                        {vacationActive ? 'Ferie aktiv nu' : vacationUpcoming ? 'Planlagt ferie' : 'Ferie afsluttet'}
                      </p>
                      <p className="text-[8px] opacity-70">{fmtDate(form.vacationFrom!)} → {fmtDate(form.vacationUntil!)}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* SPECIFIKKE ELEVER */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-black uppercase text-white/45">
                    👤 Specifikke elever
                    {hasStudentFilter && <span className="ml-2 text-yellow-400">({form.allowedStudentIds!.length} valgt)</span>}
                  </p>
                  {hasStudentFilter && (
                    <button onClick={() => setForm(f => ({...f, allowedStudentIds: []}))}
                      className="text-[9px] text-white/30 hover:text-red-400 transition-all">Ryd</button>
                  )}
                </div>
                <p className="text-[9px] text-white/25">Tom = ingen elev-begrænsning. Vælg for at vise kun bestemte elever.</p>
                {classroomStudents.length === 0 ? (
                  <p className="text-[9px] text-white/20 italic">{((classroomIds && classroomIds.length > 0) || classroomId) ? 'Ingen elever fundet' : 'Ingen classrooms tilknyttet'}</p>
                ) : (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto no-scrollbar">
                    {classroomStudents.map(st => (
                      <button key={st.id} onClick={() => toggleStudentId(st.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl border transition-all text-left ${
                          form.allowedStudentIds?.includes(st.id)
                            ? 'bg-indigo-900/60 border-indigo-500 text-white'
                            : 'bg-black/20 border-white/5 text-white/50 hover:text-white/80'}`}>
                        <div className={`w-2 h-2 rounded-full shrink-0 ${form.allowedStudentIds?.includes(st.id) ? 'bg-indigo-400' : 'bg-white/20'}`} />
                        <span className="text-[10px] font-black uppercase">{s(st.wizardName) || st.name}</span>
                        {st.realName && <span className="text-[9px] text-white/30">{st.realName}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* ALDERSGRUPPER */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-black uppercase text-white/45">
                    🎂 Aldersgrupper
                    {hasAgeFilter && <span className="ml-2 text-yellow-400">({form.allowedAgeGroups!.length} valgt)</span>}
                  </p>
                  {hasAgeFilter && (
                    <button onClick={() => setForm(f => ({...f, allowedAgeGroups: []}))}
                      className="text-[9px] text-white/30 hover:text-red-400 transition-all">Ryd</button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {AGE_GROUPS.map(ag => (
                    <button key={ag} onClick={() => toggleAgeGroup(ag)}
                      className={`px-2.5 py-1 rounded-lg text-[9px] font-black transition-all border ${
                        form.allowedAgeGroups?.includes(ag)
                          ? 'bg-indigo-600 border-indigo-400 text-white'
                          : 'bg-black/30 border-white/10 text-white/40 hover:text-white/70'}`}>
                      {AGE_LABELS[ag] || ag}
                    </button>
                  ))}
                </div>
              </div>

              {/* OPSUMMERING */}
              {(hasStudentFilter || hasAgeFilter) && (
                <div className="bg-indigo-950/40 border border-indigo-500/20 rounded-xl px-4 py-3">
                  <p className="text-[9px] text-indigo-300 font-black uppercase mb-1">Synlig for:</p>
                  {hasStudentFilter && <p className="text-[9px] text-white/60">• {form.allowedStudentIds!.length} specifikke elever</p>}
                  {hasAgeFilter && <p className="text-[9px] text-white/60">• Aldersgrupper: {form.allowedAgeGroups!.map(ag => AGE_LABELS[ag]).join(', ')}</p>}
                  <p className="text-[9px] text-white/30 mt-1 italic">OR-logik: matcher ét af kriterierne</p>
                </div>
              )}
            </div>
          )}

          {/* ─── TAB: MERE ─── */}
          {tab === 'mere' && (
            <div className="space-y-5">

              {/* NOTIFIKATION */}
              <div className="flex items-center justify-between bg-black/30 border border-white/10 rounded-xl px-4 py-3">
                <div>
                  <p className="text-[10px] font-black uppercase text-white/70">🔔 Send notifikation ved oprettelse</p>
                  <p className="text-[9px] text-white/30">Notificerer relevante elever om det nye ritual</p>
                </div>
                <button onClick={() => setNotifyOnSave(!notifyOnSave)}
                  className={`w-12 h-6 rounded-full relative transition-all ${notifyOnSave ? 'bg-green-600' : 'bg-white/10'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all shadow-sm ${notifyOnSave ? 'left-6' : 'left-0.5'}`} />
                </button>
              </div>

              {/* KOPIER TIL ANDET RUM */}
              {existing && allRooms.length > 1 && (
                <div className="space-y-2">
                  <p className="text-[10px] font-black uppercase text-white/45">📋 Kopier ritual til andet rum</p>
                  <p className="text-[9px] text-white/25">Synlighedsindstillinger nulstilles i det nye rum.</p>
                  <div className="flex gap-2">
                    <select value={copyToRoomId} onChange={e => setCopyToRoomId(e.target.value)}
                      className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-white text-sm outline-none focus:border-indigo-500/40">
                      <option value="">— Vælg rum —</option>
                      {allRooms.filter(r => r.id !== roomId).map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                    <button onClick={handleCopyToRoom} disabled={!copyToRoomId || copying}
                      className="px-4 py-2 bg-indigo-600/40 hover:bg-indigo-600 border border-indigo-500/30 rounded-xl text-[9px] font-black uppercase text-indigo-300 hover:text-white transition-all disabled:opacity-30">
                      {copying ? '...' : 'Kopier'}
                    </button>
                  </div>
                </div>
              )}

              {/* ACADEMYHALLS FAGDØRE */}
              {hallsData.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] font-black uppercase text-blue-300/70">🚪 AcademyHalls fagdøre (valgfrit)</p>
                  <p className="text-[9px] text-white/25">Tænder Live-badge automatisk i fagdøren under timen.</p>
                  <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                    {hallsData.map(hall => (
                      <div key={hall.id}>
                        <p className="text-[8px] text-orange-400/50 font-black uppercase mb-1 pl-1">{hall.name}</p>
                        <div className="space-y-1 pl-2">
                          {(hall.subjects || []).filter((subj: any) => subj.isActive !== false).map((subj: any) => {
                            const subjName = typeof subj.name === 'string' ? subj.name : subj.name?.da || subj.name?.en || 'Fagdør';
                            const linked = form.linkedSubjects || [];
                            const isLinked = linked.some((ls: any) => ls.hallId === hall.id && ls.subjectId === subj.id);
                            return (
                              <button key={subj.id}
                                onClick={() => setForm(prev => ({
                                  ...prev,
                                  linkedSubjects: isLinked
                                    ? (prev.linkedSubjects || []).filter((ls: any) => !(ls.hallId === hall.id && ls.subjectId === subj.id))
                                    : [...(prev.linkedSubjects || []), { hallId: hall.id, subjectId: subj.id }]
                                }))}
                                className={`w-full flex items-center justify-between p-2 rounded-lg border text-[9px] font-bold transition-all ${isLinked ? 'bg-blue-600/20 border-blue-500 text-blue-300' : 'bg-white/5 border-transparent text-white/30 hover:bg-white/10'}`}>
                                <span>{subjName}</span>
                                <span>{isLinked ? '🔵' : '○'}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* SLET */}
              {existing && isAdmin(currentUser.role) && (
                <div className="pt-4 border-t border-white/5">
                  <button onClick={handleDelete}
                    className="w-full py-3 bg-red-900/20 hover:bg-red-700 border border-red-500/20 rounded-xl text-[9px] font-black uppercase text-red-400 hover:text-white transition-all">
                    🗑 Slet dette ritual permanent
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="p-4 border-t border-white/10 bg-black/20 flex gap-3 shrink-0">
          <div className="flex-1" />
          <button onClick={onClose} className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-[9px] font-black uppercase text-white/40 hover:text-white transition-all">
            Annuller
          </button>
          <button onClick={handleSave} disabled={saving || !form.title?.trim()}
            className="px-6 py-2.5 bg-yellow-600 hover:bg-yellow-500 rounded-xl text-[9px] font-black uppercase text-white transition-all disabled:opacity-40">
            {saving ? 'Gemmer...' : existing ? 'Gem ændringer' : 'Opret ritual'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── HOVED KOMPONENT ──────────────────────────────────────────────────────────

const SchedulePopup: React.FC<SchedulePopupProps> = ({ roomId, currentUser, classroomId, classroomIds, accessCtx, onClose, onOpenForge, onNavigateToRoom, initialFullView }) => {
  const [rituals, setRituals] = useState<ScheduleRitual[]>([]);
  /* view-as state (skal staa FOER loaderens deps) */
  const [viewAsStudent, setViewAsStudent] = useState<any | null>(null);
  const [viewAsList, setViewAsList] = useState<any[]>([]);
  const effUser: any = viewAsStudent || currentUser;
  const [schoolVacations, setSchoolVacations] = useState<any[]>([]);
  const [showForge, setShowForge] = useState(false);
  const [editingRitual, setEditingRitual] = useState<ScheduleRitual | undefined>();
  const [editingDate, setEditingDate] = useState<string>('');
  const [noteViewP, setNoteViewP] = useState('');
  const [fullView, setFullView] = useState(initialFullView === true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [allRituals, setAllRituals] = useState<ScheduleRitual[]>([]);
  const [allRooms, setAllRooms] = useState<{ id: string; name: string }[]>([]);

  const isGuardian = isStaff(currentUser.role);

  const todayRaw = new Intl.DateTimeFormat('da-DK', { weekday: 'long' }).format(new Date());
  const today = todayRaw.charAt(0).toUpperCase() + todayRaw.slice(1);

  useEffect(() => {
    if (!roomId) return;
    const unsub = safeOnSnapshot(collection(db, `room_schedules/${roomId}/rituals`), (snap: any) => {
      const r: ScheduleRitual[] = [];
      snap.forEach((d: any) => {
        const data = { ...d.data(), id: d.id } as ScheduleRitual;
        if (canSeeRitual(data, effUser)) r.push(data);
      });
      setRituals(r.sort((a, b) => a.time.localeCompare(b.time)));
    });
    return () => unsub();
  }, [roomId, currentUser]);
  useEffect(() => {
    if (!fullView) return;
    const unsub = fbOnSnapshot(collectionGroup(db, 'rituals'), (snap: any) => {
      const r: ScheduleRitual[] = [];
      snap.forEach((d: any) => {
        const data = { ...d.data(), id: d.id, __roomId: d.ref.parent.parent?.id } as any;
        if ((data.__roomId || '').startsWith('config_')) return;
        if (canSeeRitual(data, effUser)) r.push(data);
      });
      setAllRituals(r.sort((a, b) => a.time.localeCompare(b.time)));
    });
    return () => unsub();
  }, [fullView, currentUser, viewAsStudent]);

  useEffect(() => {
    if (!isGuardian) return;
    getDocs(query(collection(db, 'users'), where('role', '==', 'student'))).then(snap => {
      const cids: string[] = (Array.isArray(classroomIds) && classroomIds.length > 0 ? classroomIds : (classroomId ? [classroomId] : [])) as string[];
      const list: any[] = [];
      snap.forEach(d => { const u: any = { id: d.id, ...(d.data() as any) }; const uc: string[] = (u.classIds || []) as string[]; if (cids.length === 0 || uc.some(c => cids.includes(c))) list.push(u); });
      list.sort((a, b) => String(a.username || '').localeCompare(String(b.username || '')));
      setViewAsList(list);
      try { const saved = sessionStorage.getItem('norewyn.campusViewAs'); if (saved) { const hit = list.find(u => u.id === saved); if (hit) setViewAsStudent(hit); } } catch (e) {}
    });
  }, [isGuardian, classroomId, classroomIds]);
  useEffect(() => {
    if (!isGuardian) return;
    getDocs(collection(db, 'rooms')).then(snap => {
      const rooms: { id: string; name: string }[] = [];
      snap.forEach(d => rooms.push({ id: d.id, name: (d.data() as any).name || d.id }));
      setAllRooms(rooms);
    });
  }, [isGuardian]);

  const weekDateFor = (dayName: string): string => {
    const idx = (DAYS as readonly string[]).indexOf(dayName);
    const mon = new Date(); mon.setDate(mon.getDate() - ((mon.getDay() + 6) % 7) + weekOffset * 7);
    const d = new Date(mon); d.setDate(d.getDate() + idx);
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  };
  const weekLabel = (() => {
    const monL = new Date(); monL.setDate(monL.getDate() - ((monL.getDay() + 6) % 7) + weekOffset * 7); monL.setHours(0, 0, 0, 0);
    const sunL = new Date(monL); sunL.setDate(sunL.getDate() + 6);
    const th = new Date(monL); th.setDate(th.getDate() + 3);
    const w1 = new Date(th.getFullYear(), 0, 4);
    const wk = 1 + Math.round(((th.getTime() - w1.getTime()) / 86400000 - 3 + (w1.getDay() + 6) % 7) / 7);
    const fm = (d: Date) => String(d.getDate()).padStart(2, '0') + '.' + String(d.getMonth() + 1).padStart(2, '0');
    return 'Uge ' + wk + ' \u00b7 ' + fm(monL) + '\u2013' + fm(sunL);
  })();
  useEffect(() => {
    const unsub = safeOnSnapshot(query(collection(db, 'school_years'), where('status', '==', 'published')), (snap: any) => {
      const vacs: any[] = [];
      snap.docs.forEach((d: any) => {
        const yr = d.data();
        (yr.vacations || []).forEach((v: any) => {
          if (!v.from || !v.to) return;
          vacs.push({ from: v.from, to: v.to, hideLessons: v.hideLessons });
        });
      });
      setSchoolVacations(vacs);
    });
    return () => { try { unsub && unsub(); } catch {} };
  }, []);

  const occursOnDate = (r: any, dateStr: string): boolean => {
    const probe = { ...r, isVisible: true, hiddenUntil: 0 };
    const skipVLive = !(r as any).visibleInVacations;
    const vacRanges = skipVLive ? schoolVacations.filter((v: any) => v.hideLessons !== false).map((v: any) => ({ from: v.from, to: v.to })) : [];
    return expandRitual(probe as any, dateStr, dateStr, vacRanges, skipVLive, true).length > 0;
  };
  const getDayException = (r: any, dateStr: string) => ((r as any).exceptions || []).find((e: any) => e.date === dateStr);
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const ritualsByDay = Object.fromEntries(DAYS.map(day => [day, rituals.filter(r => r.day === day)]));
    printWindow.document.write(`
      <!DOCTYPE html><html><head>
        <title>Norewyn Akademiet — Ugeskema</title>
        <style>
          @page { size: A4 landscape; margin: 12mm; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Georgia, serif; background: #0a0a1a; color: #e8e0cc; }
          .header { text-align: center; padding: 8mm 0 6mm; border-bottom: 1px solid #c9a84c44; margin-bottom: 5mm; }
          .title { font-size: 22pt; letter-spacing: 0.3em; text-transform: uppercase; color: #c9a84c; }
          .subtitle { font-size: 7pt; letter-spacing: 0.5em; text-transform: uppercase; color: #6666aa; margin-top: 2mm; }
          .grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2mm; height: 160mm; }
          .day-col { border: 1px solid #ffffff11; border-radius: 4mm; overflow: hidden; display: flex; flex-direction: column; }
          .day-header { padding: 2mm; text-align: center; font-size: 7pt; letter-spacing: 0.2em; text-transform: uppercase; color: #8888cc; background: #ffffff08; border-bottom: 1px solid #ffffff11; }
          .day-header.today { color: #c9a84c; background: #c9a84c11; }
          .rituals { flex: 1; padding: 2mm; display: flex; flex-direction: column; gap: 1.5mm; overflow: hidden; }
          .ritual { padding: 2mm 2.5mm; border-radius: 2mm; border-left: 2px solid #6666aa; background: #ffffff05; }
          .ritual.plus { border-left-color: #c9a84c; background: #c9a84c08; }
          .ritual.vacation { border-left-color: #f97316; opacity: 0.6; }
          .ritual-time { font-size: 6pt; color: #8888cc; font-family: monospace; }
          .ritual-title { font-size: 7.5pt; color: #e8e0cc; font-weight: bold; margin-top: 0.5mm; line-height: 1.3; }
          .ritual-guardian { font-size: 5.5pt; color: #8888cc; margin-top: 0.5mm; font-style: italic; }
          .ritual-type { font-size: 5.5pt; text-transform: uppercase; letter-spacing: 0.1em; color: #6666aa; }
          .badge { font-size: 5pt; padding: 0.5mm 1mm; border-radius: 1mm; margin-left: 1mm; }
          .plus-badge { background: #c9a84c; color: #000; }
          .vacation-badge { background: #f97316; color: #000; }
          .empty { flex: 1; display: flex; align-items: center; justify-content: center; opacity: 0.15; font-size: 16pt; }
          .footer { margin-top: 4mm; text-align: center; font-size: 6pt; color: #444466; letter-spacing: 0.3em; text-transform: uppercase; border-top: 1px solid #c9a84c22; padding-top: 3mm; }
        </style>
      </head><body>
        <div class="header">
          <div class="title">✦ Norewyn Akademiet ✦</div>
          <div class="subtitle">Akademiets Master-Grimoire — Ugentligt Skema</div>
        </div>
        <div class="grid">
          ${DAYS.map(day => {
            const isToday = day === today;
            const dayRits = ritualsByDay[day] || [];
            return `<div class="day-col">
              <div class="day-header ${isToday ? 'today' : ''}">${day}${isToday ? ' ◆' : ''}</div>
              <div class="rituals">
                ${dayRits.length > 0 ? dayRits.map(r => {
                  const now = Date.now();
                  const onVacation = r.vacationFrom && r.vacationUntil && now >= r.vacationFrom && now <= r.vacationUntil;
                  return `<div class="ritual ${r.accessLevel === 'plus' ? 'plus' : ''} ${onVacation ? 'vacation' : ''}">
                    <div class="ritual-time">${r.time}${r.endTime ? ' – ' + r.endTime : ''}</div>
                    <div class="ritual-title">${r.title}${r.accessLevel === 'plus' ? '<span class="badge plus-badge">Plus</span>' : r.accessLevel === 'standard' ? '<span class="badge" style="background:#3b82f6;color:#fff">Std</span>' : ''}${onVacation ? '<span class="badge vacation-badge">🏖️</span>' : ''}</div>
                    ${r.assignedGuardianName ? `<div class="ritual-guardian">🧑‍🏫 ${r.assignedGuardianName}</div>` : ''}
                    ${r.description ? `<div class="ritual-guardian">${r.description}</div>` : ''}
                    <div class="ritual-type">${r.type}</div>
                  </div>`;
                }).join('') : '<div class="empty">📜</div>'}
              </div>
            </div>`;
          }).join('')}
        </div>
        <div class="footer">— Norewyn Academy — Kun til intern brug —</div>
        <script>window.onload = () => { window.print(); window.onafterprint = () => window.close(); }</script>
      </body></html>
    `);
    printWindow.document.close();
  };

  return (
    <>
      <div className="fixed inset-0 z-[600] flex items-center justify-center p-2 md:p-6 bg-black/80 backdrop-blur-md animate-fade-in">
        <div className="w-full max-w-[95vw] xl:max-w-7xl bg-indigo-950/90 border-2 border-indigo-400/30 rounded-[2rem] md:rounded-[3rem] shadow-[0_0_100px_rgba(0,0,0,1)] overflow-hidden flex flex-col h-[90vh] md:h-[85vh]">

          {/* HEADER */}
          <div className="p-3 md:p-10 border-b border-white/5 bg-indigo-900/30 flex justify-between items-center shrink-0 gap-2">
            <div className="flex items-center gap-4 md:gap-6">
              <span className="text-2xl md:text-5xl drop-shadow-[0_0_15px_rgba(234,179,8,0.5)]">📅</span>
              <div>
                <h2 className="text-sm sm:text-base md:text-3xl font-cinzel text-white tracking-tight md:tracking-widest uppercase leading-tight">Akademiets Master-Grimoire</h2>
                <p className="text-[8px] md:text-[10px] font-black uppercase text-indigo-300 tracking-[0.4em] opacity-60">Ugentlig tidslinje for dette rum</p>
              </div>
            </div>
            <div className="flex items-center flex-wrap justify-end gap-2 md:gap-4">
              {isGuardian && (
                <button onClick={() => { setEditingRitual(undefined); setEditingDate(''); setShowForge(true); }}
                  className="flex items-center gap-2 md:gap-3 bg-yellow-600/20 hover:bg-yellow-600 text-yellow-400 hover:text-white px-3 md:px-6 py-2 md:py-3 rounded-xl md:rounded-2xl border border-yellow-500/30 transition-all group shrink-0">
                  <span className="text-lg md:text-xl group-hover:animate-bounce">📜</span>
                  <span className="text-[8px] md:text-[10px] font-black uppercase tracking-widest hidden sm:inline">+ Nyt Ritual</span>
                </button>
              )}
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setWeekOffset(o => o - 1)} className="px-2 md:px-3 py-2 md:py-3 rounded-xl border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition text-xs font-black">{'\u2190'}</button>
                <div className="text-center px-1">
                  <p className="hidden sm:block text-[8px] md:text-[9px] font-mono font-black text-yellow-300/90 whitespace-nowrap">{weekLabel}</p>
            {isGuardian && viewAsList.length > 0 && (<div className="flex items-center gap-2 bg-purple-950/30 border border-purple-500/30 rounded-xl px-3 py-1.5"><span>{'\u{1F441}\uFE0F'}</span><select value={viewAsStudent?.id || ''} onChange={e => { const u = viewAsList.find(x => x.id === e.target.value) || null; setViewAsStudent(u); try { if (u) sessionStorage.setItem('norewyn.campusViewAs', u.id); else sessionStorage.removeItem('norewyn.campusViewAs'); } catch (er) {} }} className="bg-black border border-purple-500/30 rounded-lg px-2 py-1 text-white text-xs"><option value="">{'Min egen visning'}</option>{viewAsList.map(u => <option key={u.id} value={u.id}>{'\u{1F9D9} '}{u.username}</option>)}</select>{viewAsStudent && <button onClick={() => { setViewAsStudent(null); try { sessionStorage.removeItem('norewyn.campusViewAs'); } catch (er) {} }} className="text-purple-300 text-xs font-bold">{'\u2715'}</button>}</div>)}
                  {weekOffset !== 0 && <button onClick={() => setWeekOffset(0)} className="text-[8px] font-black uppercase text-indigo-300 underline hover:text-white">I dag</button>}
                </div>
                <button onClick={() => setWeekOffset(o => o + 1)} className="px-2 md:px-3 py-2 md:py-3 rounded-xl border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition text-xs font-black">{'\u2192'}</button>
              </div>
              <button onClick={() => setFullView(v => v === false)} className={`flex items-center gap-2 px-3 md:px-5 py-2 md:py-3 rounded-xl md:rounded-2xl border transition-all shrink-0 text-[10px] font-black uppercase ${fullView ? 'bg-indigo-600 border-indigo-400 text-white' : 'bg-indigo-600/20 border-indigo-500/30 text-indigo-300 hover:bg-indigo-600 hover:text-white'}`}>{'\uD83C\uDF10'} {fullView ? 'Dette rum' : 'Mit fulde skema'}</button>
              {isGuardian && onOpenForge && (
                <button onClick={onOpenForge} className="flex items-center gap-2 md:gap-3 bg-orange-600/20 hover:bg-orange-600 text-orange-400 hover:text-white px-3 md:px-6 py-2 md:py-3 rounded-xl md:rounded-2xl border border-orange-500/30 transition-all group shrink-0">
                  <span className="text-lg md:text-xl group-hover:animate-bounce">⚒️</span>
                  <span className="text-[8px] md:text-[10px] font-black uppercase tracking-widest hidden sm:inline">Ret Skema</span>
                </button>
              )}
              <button onClick={onClose} className="text-white/40 hover:text-white text-3xl md:text-5xl transition-colors leading-none">&times;</button>
            </div>
          </div>

          {/* GRID */}
          <div className="flex-1 overflow-x-auto overflow-y-hidden bg-black/20 flex" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(129,140,248,0.45) transparent' }}>
            <div className="hidden xl:flex flex-col w-20 border-r border-white/5 bg-black/40 pt-20 pb-10 shrink-0 select-none">
              {['08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'].map(t => (
                <div key={t} className="flex-1 flex items-start justify-center text-[9px] font-black text-yellow-500/70 uppercase tracking-tighter">{t}</div>
              ))}
            </div>

            <div className="flex min-w-max xl:min-w-0 xl:flex-1 h-full">
              {DAYS.map(day => {
                const colDate = weekDateFor(day);
                const dayRituals = (fullView ? allRituals : rituals).filter(r => r.day === day && occursOnDate(r, colDate));
                const isToday = weekOffset === 0 && day === today;
                return (
                  <div key={day} className={`flex flex-col w-[80vw] sm:w-[45vw] md:w-[30vw] xl:w-auto xl:flex-1 border-r border-white/5 transition-all duration-700 relative group ${isToday ? 'bg-yellow-500/[0.03] z-10' : 'hover:bg-white/[0.02]'}`}>
                    <div className={`p-4 md:p-6 text-center border-b border-white/5 shrink-0 transition-colors ${isToday ? 'bg-yellow-500/10' : 'bg-black/20'}`}>
                      <h3 className={`text-[10px] md:text-xs font-cinzel uppercase tracking-[0.3em] transition-all ${isToday ? 'text-yellow-500 scale-110 drop-shadow-[0_0_8px_rgba(234,179,8,0.5)]' : 'text-indigo-300 opacity-60'}`}>{day}</h3>
                      <div className="text-[8px] text-white/25 font-mono mt-0.5">{colDate.slice(5)}</div>
                      {isToday && <div className="text-[7px] font-black text-yellow-500/60 uppercase tracking-widest mt-1">Nuværende dag</div>}
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3 pb-20" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(129,140,248,0.45) transparent' }}>
                      {dayRituals.length > 0 ? dayRituals.map(r => {
                        const now = Date.now();
                        const dayEx = getDayException(r, colDate);
                        const isCancelledToday = !!(dayEx && dayEx.cancelled);
                        const exPatch = dayEx && dayEx.patch ? dayEx.patch : null;
                        const isHiddenByDate = !!(r.hiddenUntil && now < r.hiddenUntil);
                        const isOnVacation = !!(r.vacationFrom && r.vacationUntil && now >= r.vacationFrom && now <= r.vacationUntil);
                        const vacationUpcoming = !!(r.vacationFrom && r.vacationUntil && now < r.vacationFrom);
                        const isEffectiveHidden = r.isVisible === false || isHiddenByDate || isOnVacation;
                        const isPlus = r.accessLevel === 'plus';
                        const isStandard = r.accessLevel === 'standard';
                        const isBasis = r.accessLevel === 'basis';
                        const hasPersonalFilter = (r.allowedStudentIds?.length || 0) > 0 || (r.allowedAgeGroups?.length || 0) > 0;

                        return (
                          <div key={r.id}
                            className={`relative p-4 rounded-2xl border transition-all animate-fade-in group/card
                              ${isEffectiveHidden && !isOnVacation ? 'bg-black/40 border-red-900/30 opacity-40'
                                : isOnVacation ? 'bg-orange-950/20 border-orange-500/20 opacity-60'
                                : isPlus ? 'bg-amber-500/[0.08] border-amber-500/30 hover:border-amber-500 hover:bg-amber-500/20 shadow-xl'
                                : isStandard ? 'bg-blue-500/[0.06] border-blue-500/20 hover:border-blue-400/50 shadow-xl'
                                : isBasis ? 'bg-green-500/[0.06] border-green-500/20 hover:border-green-400/50 shadow-xl'
                                : 'bg-white/[0.03] border-white/5 hover:border-indigo-400/40 hover:bg-white/[0.07] hover:-translate-y-1 shadow-xl'
                              }`}
                            onClick={() => isGuardian && (setEditingRitual(r), setEditingDate(colDate), setShowForge(true))}
                            style={{ cursor: isGuardian ? 'pointer' : 'default' }}
                          >
                            <div className="flex flex-col gap-1.5">
                              <div className="flex justify-between items-start">
                                <span className={`text-[10px] md:text-xs font-mono font-black ${isPlus ? 'text-amber-400' : isStandard ? 'text-blue-400' : 'text-indigo-300'}`}>
                                  {(exPatch && exPatch.time) || r.time}{((exPatch && exPatch.endTime) || r.endTime) ? ` – ${(exPatch && exPatch.endTime) || r.endTime}` : ''}
                                </span>
                                <div className="flex gap-1 flex-wrap justify-end">
                                  {isPlus && <span className="text-[7px] font-black uppercase px-1.5 py-0.5 rounded bg-amber-500 text-black">💎</span>}
                                  {isStandard && <span className="text-[7px] font-black uppercase px-1.5 py-0.5 rounded bg-blue-600 text-white border border-blue-400/40">Std</span>}
                                  {isBasis && <span className="text-[7px] font-black uppercase px-1.5 py-0.5 rounded bg-green-700 text-white border border-green-500/40">Basis</span>}
                                  {hasPersonalFilter && isGuardian && <span className="text-[7px] font-black uppercase px-1.5 py-0.5 rounded bg-indigo-600/60 text-indigo-300 border border-indigo-500/30">👤</span>}
                                  <span className={`text-[7px] font-black uppercase px-1.5 py-0.5 rounded border ${r.type === 'Lektion' ? 'border-blue-500/40 text-blue-400/80' : r.type === 'Ritual' ? 'border-purple-500/40 text-purple-400/80' : r.type === 'Samling' ? 'border-yellow-500/40 text-yellow-400/80' : 'border-red-500/40 text-red-400/80'}`}>
                                    {r.type.charAt(0)}
                                  </span>
                                </div>
                              </div>
                              <h4 className={`text-[11px] md:text-[13px] font-bold leading-tight ${isPlus ? 'text-amber-100' : 'text-white/90'}`}>
                                <span className={isCancelledToday && !((dayEx as any) || {}).statusLabel ? 'line-through opacity-50' : ''}>{(exPatch && exPatch.title) || r.title}</span>
                                {fullView && (r as any).__roomId && (r as any).__roomId !== roomId && accessCtx?.rooms?.[(r as any).__roomId] && <button onClick={e => { e.stopPropagation(); onNavigateToRoom?.((r as any).__roomId); onClose(); }} className="ml-2 text-[8px] font-bold bg-indigo-900/60 border border-indigo-500/40 text-indigo-200 px-1.5 py-0.5 rounded uppercase hover:bg-indigo-600">{'\uD83D\uDCCD'} {accessCtx.rooms[(r as any).__roomId].name}</button>}{(isCancelledToday || (dayEx && (dayEx as any).statusLabel)) && <span className={"ml-2 text-[8px] font-black text-white px-1.5 py-0.5 rounded uppercase align-middle " + (((dayEx as any) || {}).statusLabel ? 'bg-amber-600' : 'bg-red-600')}>{((dayEx as any) || {}).statusLabel || 'Aflyst i dag'}</span>}
                                
                                {dayEx && (dayEx as any).note && <p className="text-[11px] text-amber-200/90 italic mt-1 leading-snug cursor-pointer hover:text-amber-100" onClick={e => { e.stopPropagation(); setNoteViewP((dayEx as any).note); }}>{'\uD83D\uDCE2'} {(dayEx as any).note}</p>}
                                {exPatch && !isCancelledToday && <span className="ml-1 text-[9px] text-amber-400">{'\u270E'}</span>}
                              </h4>
                              {r.assignedGuardianName && (
                                <p className="text-[9px] text-indigo-300/60 italic">🧑‍🏫 {(dayEx && (dayEx as any).coverGuardianName) || r.assignedGuardianName}{dayEx && (dayEx as any).coverGuardianName ? ' (afløser)' : ''}</p>
                              )}
                              {r.description && <p className="text-[9px] md:text-[10px] text-indigo-200/40 italic line-clamp-2 leading-relaxed">{r.description}</p>}
              {(r as any).targetRoomId && String((r as any).targetRoomId) !== roomId && String((r as any).targetRoomId) !== "0" && (
                <button
                  onClick={e => { e.stopPropagation(); onNavigateToRoom?.((r as any).targetRoomId); onClose(); }}
                  className="mt-1 w-full bg-yellow-500/15 hover:bg-yellow-500/30 border border-yellow-500/30 text-yellow-400 text-[9px] font-black uppercase rounded-lg py-1.5 transition-all flex items-center justify-center gap-1"
                >
                  🚪 Gå til dette rum
                </button>
              )}

                              {isGuardian && isEffectiveHidden && !isOnVacation && (
                                <div className="text-[7px] bg-red-900/40 text-red-300 px-2 py-0.5 rounded uppercase font-black text-center mt-1">Skjult for elever</div>
                              )}
                              {isGuardian && isOnVacation && (
                                <div className="text-[7px] bg-orange-900/40 text-orange-300 px-2 py-0.5 rounded uppercase font-black text-center mt-1">🏖️ Ferie til {fmtDate(r.vacationUntil!)}</div>
                              )}
                              {isGuardian && vacationUpcoming && (
                                <div className="text-[7px] bg-yellow-900/30 text-yellow-300/70 px-2 py-0.5 rounded uppercase font-black text-center mt-1">📅 Ferie fra {fmtDate(r.vacationFrom!)}</div>
                              )}
                            </div>
                          </div>
                        );
                      }) : (
                        <div className="h-full flex flex-col items-center justify-center opacity-30 pointer-events-none pt-20">
                          <span className="text-3xl mb-2">📜</span>
                          <span className="text-[8px] font-black uppercase tracking-widest">Intet rituale</span>
                        </div>
                      )}
                    </div>
                    {isToday && <div className="absolute bottom-0 inset-x-0 h-1 bg-yellow-500 shadow-[0_0_20px_rgba(234,179,8,0.8)]" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* FOOTER */}
          <div className="p-4 bg-black/40 border-t border-white/5 flex justify-between items-center shrink-0">
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Lektion', color: 'bg-blue-500' },
                { label: 'Ritual', color: 'bg-purple-500' },
                { label: 'Basis', color: 'bg-green-600' },
                { label: 'Standard', color: 'bg-blue-600' },
                { label: 'Plus', color: 'bg-amber-500' },
              ].map(({ label, color }) => (
                <div key={label} className="flex items-center gap-1.5">
                  <div className={`w-1.5 h-1.5 rounded-full ${color}`} />
                  <span className="text-[8px] font-black uppercase text-indigo-400/60 hidden sm:inline">{label}</span>
                </div>
              ))}
              {isGuardian && (
                <div className="flex items-center gap-1.5 ml-2">
                  <span className="text-[7px] bg-orange-900/40 text-orange-300 px-1 py-0.5 rounded">🏖️</span>
                  <span className="text-[8px] font-black uppercase text-indigo-400/60 hidden sm:inline">Ferie</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <p className="text-[8px] font-black text-white/50 uppercase tracking-widest italic md:hidden">Svejs til siden for at se hele ugen på mobilen</p>
              <button onClick={handlePrint} className="flex items-center gap-2 bg-amber-500/10 hover:bg-amber-500 text-amber-400 hover:text-black px-4 py-2 rounded-xl border border-amber-500/20 transition-all text-[8px] font-black uppercase tracking-widest">
                📄 Gem som PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {noteViewP && (
        <div className="fixed inset-0 z-[999] bg-black/70 flex items-center justify-center p-6" onClick={() => setNoteViewP('')}>
          <div className="bg-[#1a1330] border border-amber-500/40 rounded-2xl p-5 max-w-md text-amber-100 text-sm leading-relaxed shadow-2xl" onClick={e => e.stopPropagation()}>{'\uD83D\uDCE2'} {noteViewP}</div>
        </div>
      )}
      {showForge && (
        <ForgeModal
          roomId={roomId}
          classroomId={classroomId}
          classroomIds={classroomIds}
          accessCtx={accessCtx}
          existing={editingRitual}
          editDate={editingDate}
          currentUser={currentUser}
          allRooms={allRooms}
          onClose={() => { setShowForge(false); setEditingRitual(undefined); }}
        />
      )}
    </>
  );
};

export default SchedulePopup;